import { Checkbox } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { FormGroup } from '@mui/material';
import { TextField } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { EditRubricPopup } from 'apps/sigma2-ui/src/components/rubrics/EditRubricPopup';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { toSvgColor } from 'apps/sigma2-ui/src/components/svg/types';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TertiaryButton } from 'apps/sigma2-ui/src/components/TertiaryButton';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';

interface EditRubricTagsPopupProps {
  anchorEl: HTMLAnchorElement | null;
  open: boolean;
  onClose: () => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  tags: string[];
  possibleTags: string[];
}

const EditRubricTagsPopupSearchIcon: React.FC<SvgProps> = (props) => {
  const strokeColor = toSvgColor('currentColor', props);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.0016 13.9996L11.1016 11.0996"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const EditRubricTagsPopup: React.FC<EditRubricTagsPopupProps> = ({
  anchorEl,
  open,
  onClose,
  possibleTags,
  onAddTag,
  onRemoveTag,
  tags,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTags = useMemo(
    () =>
      possibleTags.filter((t) =>
        t.toLocaleLowerCase().startsWith(searchQuery.toLocaleLowerCase()),
      ),
    [searchQuery, possibleTags],
  );

  const hasSearchResults = useMemo(
    () => filteredTags.length > 0,
    [filteredTags.length],
  );

  const hasEnteredSearchQuery = useMemo(
    () => searchQuery.length > 0,
    [searchQuery],
  );

  const isChecked = useCallback((name: string) => tags.includes(name), [tags]);

  const handleSelectChanged = useCallback(
    (name: string, selected: boolean) => {
      if (selected) {
        onAddTag(name);
      } else {
        onRemoveTag(name);
      }
    },
    [onAddTag, onRemoveTag],
  );

  const language = useLanguage();
  return (
    <EditRubricPopup anchorEl={anchorEl} open={open} onClose={onClose}>
      <div className="w-[324px] pb-2">
        <Spacing
          direction="horizontal"
          size="sm"
          className="items-center py-4 px-6"
        >
          <EditRubricTagsPopupSearchIcon />
          <TextField
            variant="standard"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            placeholder={language.editRubricTagsSearchPlaceholder()}
            className="noFormControlPad"
            autoFocus
            sx={{
              '&.MuiFormControl-root.noFormControlPad': {
                paddingTop: 0,
              },
            }}
            slotProps={{
              input: {
                sx: {
                  fontWeight: 500,
                  fontSize: 16,
                  '&:before': {
                    borderBottom: 'none',
                  },
                  '&:after': {
                    borderBottom: 'none',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none',
                  },
                },
              },
              htmlInput: {
                sx: {
                  paddingBottom: 0,
                },
              },
            }}
          />
          {!hasSearchResults && hasEnteredSearchQuery && (
            <Spacing direction="horizontal" size="md" className="items-center">
              <TertiaryButton
                label={language.create()}
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    onAddTag(searchQuery);
                    onClose();
                    setSearchQuery('');
                  },
                }}
                typographyClassName="text-indigo-1000"
              />
              <TouchTarget
                className="text-indigo-1000"
                behavior={{
                  type: 'onClick',
                  onClick: () => setSearchQuery(''),
                }}
                content={() => <IconCloseSvg svg={{ className: 'w-5 h-5' }} />}
                label={language.remove()}
              />
            </Spacing>
          )}
        </Spacing>
        <hr className="h-[1px] bg-gray-100 w-full" />
        <FormGroup>
          <Spacing
            direction="vertical"
            size="lg"
            className="pl-3 pr-6 max-h-[180px] overflow-y-auto"
          >
            {filteredTags.map((t) => (
              <FormControlLabel
                key={t}
                control={
                  <Checkbox
                    className="text-indigo-1000"
                    checked={isChecked(t)}
                    onChange={(_, checked) => handleSelectChanged(t, checked)}
                  />
                }
                label={t}
              />
            ))}
          </Spacing>
        </FormGroup>
        {!hasSearchResults && (
          <div className="flex flex-row items-center justify-center py-3 px-6">
            <S2Typography
              variant="body-6"
              textColorVariant="gray-700"
              translatedContent={language.noResults()}
            />
          </div>
        )}
      </div>
    </EditRubricPopup>
  );
};
